import React, { useEffect, useMemo } from "react";
import { Button, Empty, Tabs } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useHistory, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRotateLeft } from "@fortawesome/free-solid-svg-icons";
import Header from "../../../Components/Header/Header";
import Spinner from "../../../Components/Spinner";
import { R_AUDBILITY_USERS } from "../../../constants/routes";
import { PAGE_TITLE } from "../shared/constants";
import AvailableScreeningsCard from "./components/AvailableScreeningsCard/AvailableScreeningsCard";
import PurchaseHistory from "./components/PurchaseHistory/PurchaseHistory";
import TabLabel from "./components/TabLabel/TabLabel";
import UserLogs from "./components/UserLogs/UserLogs";
import UserPatients from "./components/UserPatients/UserPatients";
import UserSummary from "./components/UserSummary/UserSummary";
import {
  BACK_LABEL,
  DETAILS_TABS,
  RELOAD_LABEL,
  SCREENINGS_PT_BR_LABEL,
  SCREENINGS_PT_EU_LABEL,
  USER_NOT_FOUND_LABEL,
} from "./constants";
import { useAudbilityUserDetails } from "./hooks/useAudbilityUserDetails";
import classes from "./AudbilityUserDetails.module.scss";

const AudbilityUserDetails = () => {
  const history = useHistory();
  const { sub } = useParams();
  const {
    userDetails,
    isLoading,
    getUserDetails,
    logs,
    isLoadingLogs,
    getUserLogs,
    patients,
    isLoadingPatients,
    getUserPatients,
  } = useAudbilityUserDetails(sub);

  useEffect(() => {
    getUserDetails();
  }, [getUserDetails]);

  const tabItems = useMemo(
    () =>
      userDetails
        ? [
            {
              key: DETAILS_TABS.purchases.key,
              label: DETAILS_TABS.purchases.label,
              children: (
                <PurchaseHistory
                  purchases={userDetails.purchases}
                  purchasedCount={userDetails.purchasedScreenings}
                  consumedCount={userDetails.consumedScreenings}
                />
              ),
            },
            {
              key: DETAILS_TABS.patients.key,
              label: (
                <TabLabel
                  label={DETAILS_TABS.patients.label}
                  count={userDetails.patientsCount}
                />
              ),
              children: (
                <UserPatients
                  patients={patients}
                  patientsCount={userDetails.patientsCount}
                  isLoading={isLoadingPatients}
                />
              ),
            },
            {
              key: DETAILS_TABS.logs.key,
              label: DETAILS_TABS.logs.label,
              children: (
                <UserLogs logs={logs} isLoading={isLoadingLogs} />
              ),
            },
          ]
        : [],
    [userDetails, logs, isLoadingLogs, patients, isLoadingPatients]
  );

  const renderContent = () => {
    if (isLoading) return <Spinner />;

    if (!userDetails) return <Empty description={USER_NOT_FOUND_LABEL} />;

    return (
      <>
        <div className={classes.SummaryArea}>
          <UserSummary
            name={userDetails.name}
            email={userDetails.email}
            status={userDetails.status}
            statusKey={userDetails.statusKey}
            createdAt={userDetails.createdAt}
          />
          <div className={classes.ScreeningsCards}>
            <AvailableScreeningsCard
              label={SCREENINGS_PT_BR_LABEL}
              total={userDetails.availableScreeningsPtBr}
            />
            <AvailableScreeningsCard
              label={SCREENINGS_PT_EU_LABEL}
              total={userDetails.availableScreeningsPtEu}
            />
          </div>
        </div>

        <Tabs
          className={classes.DetailsTabs}
          defaultActiveKey={DETAILS_TABS.purchases.key}
          items={tabItems}
          onChange={(tabKey) => {
            if (tabKey === DETAILS_TABS.logs.key) getUserLogs();
            if (tabKey === DETAILS_TABS.patients.key) getUserPatients();
          }}
        />
      </>
    );
  };

  return (
    <div className={classes.AudbilityUserDetailsPage}>
      <div className={classes.TopBar}>
        <div className={classes.TitleArea}>
          <Header text={PAGE_TITLE} />
          <Button
            shape="circle"
            aria-label={RELOAD_LABEL}
            className={classes.ReloadButton}
            icon={<FontAwesomeIcon icon={faArrowRotateLeft} />}
            loading={isLoading}
            onClick={getUserDetails}
          />
        </div>

        <Button
          type="text"
          className={classes.BackButton}
          icon={<ArrowLeftOutlined />}
          onClick={() => history.push(R_AUDBILITY_USERS)}
        >
          {BACK_LABEL}
        </Button>
      </div>

      {renderContent()}
    </div>
  );
};

export default AudbilityUserDetails;
